<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <rest-form
      v-if="model"
      ref="form"
      :model="model"
      :rules="rules"
      :save-endpoint="saveEndpoint"
      :disabled="readonly"
    >
      <form-section :title="$t('Profiles_Title_BasicData')">
        <template slot="controls" v-if="!readonly">
          <div v-if="$can('Profile::Update') && isEdit">
            <el-button
              @click="isEdit = false"
              icon="el-icon-circle-close"
              type="primary"
              plain
            >
              {{ $t('Profiles_Button_Cancel') }}
            </el-button>
            <save-button :click="save" />
          </div>
          <el-button
            v-else-if="$can('Profile::Update')"
            type="primary"
            plain
            @click="isEdit = !isEdit"
            icon="el-icon-edit-outline"
          >
            {{ $t('Profiles_Button_EditData') }}
          </el-button>
        </template>

        <el-row v-if="$can('Profile::Update') && isEdit">
          <el-col v-for="(item, index) in items" :key="index" :span="6">
            <rest-form-item :label="item.label" :prop="item.prop">
              <span v-if="item.inputType === 'select'">
                <el-select
                  v-if="item.show"
                  v-model="model[item.prop]"
                  :filterable="item.filterable"
                >
                  <el-option
                    v-for="(option, timeIndex) in item['options']"
                    :key="timeIndex"
                    :value="timeIndex"
                    :label="option"
                  ></el-option>
                </el-select>
                <el-input
                  v-else
                  :placeholder="$t('Profiles_ValueObscured')"
                  disabled
                />
              </span>
              <span v-else>
                <el-input
                  v-model="model[item.prop]"
                  v-if="item.show"
                  :disabled="item.disabled"
                />
                <el-input
                  v-else
                  :placeholder="$t('Profiles_ValueObscured')"
                  disabled
                />
              </span>
            </rest-form-item>
          </el-col>
        </el-row>

        <el-row v-else-if="$can('Profile::Read')">
          <el-col v-for="(item, index) in items" :key="index" :span="6">
            <rest-form-item :label="item.label" :prop="item.prop">
              <div v-if="item.show">
                <overflow-tooltip
                  v-if="item.showOptionsValue"
                  :value="
                    validateValueEmpty(item.options[staticModel[item.prop]])
                  "
                />
                <overflow-tooltip
                  v-else
                  :value="validateValueEmpty(staticModel[item.prop])"
                />
              </div>
              <span v-else>
                *********
              </span>
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </rest-form>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import OverflowTooltip from '@/common/components/form/OverflowTooltip'
import { projectEndpoint } from '@/core/helpers'
import SaveButton from '@/common/components/form/SaveButton'
import parsePhoneNumber from 'libphonenumber-js'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'ProfileBasicDataCard',

  components: {
    SaveButton,
    RestPage,
    RestForm,
    FormSection,
    RestFormItem,
    OverflowTooltip
  },

  props: {
    readonly: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      isEdit: false,
      model: null,
      staticModel: {},
      rules: {
        mobile_number: {
          validator: (rule, value, callback) => {
            if (!value) {
              callback()
            } else {
              if (!value.startsWith('+')) {
                value = '+' + value
              }
              const phoneNumber = parsePhoneNumber(value)
              if (
                $hasFeature('disableMobileValidation') ||
                (!isNaN(value) && phoneNumber && phoneNumber.isValid())
              ) {
                callback()
              } else {
                callback(new Error())
              }
            }
          },
          message: this.$t('Profiles_MobileNumber_IncorrectPhoneNumber'),
          trigger: 'change'
        },
        whatsapp_number: {
          validator: (rule, value, callback) => {
            if (!value) {
              callback()
            } else {
              if (!value.startsWith('+')) {
                value = '+' + value
              }
              const phoneNumber = parsePhoneNumber(value)
              if (
                $hasFeature('disableMobileValidation') ||
                (!isNaN(value) && phoneNumber && phoneNumber.isValid())
              ) {
                callback()
              } else {
                callback(new Error())
              }
            }
          },
          message: this.$t('Profiles_MobileNumber_IncorrectPhoneNumber'),
          trigger: 'change'
        }
      },
      timezones: [],
      languages: []
    }
  },

  computed: {
    fetchEndpoint() {
      return [
        this.fetchModelEndpoint,
        this.fetchTimezonesEndpoint,
        this.fetchLanguagesEndpoint
      ]
    },

    items() {
      let items = [
        {
          label: this.$t('Profiles_Field_UserId'),
          prop: 'user_id',
          show: this.$can('UserPersonalData::Read'),
          disabled:
            this.staticModel['user_id'] !== null &&
            this.staticModel['user_id'] !== ''
        },
        {
          label: this.$t('Profiles_Field_CustomerId'),
          prop: 'customer_id',
          show: this.$can('UserPersonalData::Read'),
          disabled: !!this.staticModel['customer_id']
        },
        {
          label: this.$t('Profiles_Field_Email'),
          prop: 'email',
          show: this.$can('UserPersonalData::Read')
        },
        {
          label: this.$t('Profiles_Field_MobileNumber'),
          prop: 'mobile_number',
          show: this.$can('UserPersonalData::Read')
        },
        {
          label: this.$t('Profiles_Field_WhatsappNumber'),
          prop: 'whatsapp_number',
          show: this.$can('UserPersonalData::Read')
        },
        {
          label: this.$t('Profiles_Field_Language'),
          prop: 'overrideLanguage',
          show: true,
          inputType: 'select',
          filterable: true,
          options: this.languages,
          showOptionsValue: true
        },
        {
          label: this.$t('Profiles_Field_Timezone'),
          prop: 'overrideTimezone',
          show: true,
          inputType: 'select',
          filterable: true,
          options: this.timezones
        }
      ]
      if (!this.$store.state.app.features.customerIdEnabled) {
        return items.filter(item => item.prop !== 'customer_id')
      }
      return items
    },

    saveEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: `user-profiles/${this.$route.query.profile_id}`
      })
    },

    fetchModelEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles/${this.$route.query.profile_id}`,
        handler: data => {
          this.model = data
          Object.assign(this.staticModel, data)
        }
      })
    },

    fetchTimezonesEndpoint() {
      return {
        method: 'get',
        url: `timezones`,
        handler: data => {
          this.timezones = data
        }
      }
    },

    fetchLanguagesEndpoint() {
      return {
        method: 'get',
        url: `languages`,
        handler: data => {
          if (!this.$store.state.app.features.allowFiveCharacterLanguageCode) {
            for (let key of Object.keys(data)) {
              if (key.length > 2) {
                delete data[key]
              }
            }
          }
          this.languages = this.sortLanguages(data)
        }
      }
    }
  },

  methods: {
    save() {
      this.$refs['form'].save().then(() => {
        this.staticModel = Object.assign({}, this.staticModel, this.model)
        this.$emit('update-email', this.staticModel.email)
        this.isEdit = false
      })
    },

    validateValueEmpty(propValue) {
      if (propValue !== null && propValue !== undefined && propValue !== '') {
        return propValue
      } else {
        return '-'
      }
    },

    sortLanguages(languages) {
      const orderedArray = Object.values(languages).sort()
      let ordered = {}

      orderedArray.forEach(element => {
        let key = Object.keys(languages).find(
          item => languages[item] === element
        )
        ordered[key] = element
      })
      return ordered
    }
  }
}
</script>

<style scoped>
.el-col.el-col-6 {
  margin-bottom: 20px;
}

.el-col {
  height: 66px;
}

>>> .content {
  margin-top: 30px;
  background-color: #f8fafc;
  padding: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/components/ProfileBasicDataCard.vue